import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { FaRegTrashAlt } from "react-icons/fa";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import { MdAdd } from "react-icons/md";
import { Button, Input } from "rizzui";
import { toast } from "sonner";
import * as yup from "yup";
import ButtonPopover from "../../components/buttons/ButtonPopover";
import CustomSelect from "../../components/forms/CustomSelect";
import RequiredInput from "../../components/forms/InputHOC";
import PhoneInput from "../../components/forms/PhoneInputWrapper";
import { Loader } from "../../components/loader/SpinnerLogo";
import { usePrivilege } from "../../components/priviledge/PriviledgeProvider";
import useOrganisingCorporations from "../../hooks/api-calls/networks/useOrganisingCorporations";
import useDepartments, { useUnassignedDepartments } from "../../hooks/api-calls/useDepartments";
import useNetworks from "../../hooks/api-calls/useNetworks";
import useProductTypes from "../../hooks/api-calls/useProductTypes";
import useUsers from "../../hooks/api-calls/useUsers";
import useFetch from "../../hooks/useFetch";
import useHandleErrors from "../../hooks/useHandleErrors";
import Page from "../../layout/Page/Page";
import FormGroup from "../../layout/shared/form-group";
import { NetworkService } from "../../services/network/networkService";
import { Network } from "../../type/entities/network-type";
import FormFooter from "../_layout/_footers/form-footer";

interface CreateFormProps {
    id: string | undefined;
    setNodeId: (value: string | undefined) => void;
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
    refetch: () => void;
}

const schema = yup.object({
    name: yup.string().min(1, 'Demasiado corto').max(50, 'Demasiado largo').required('Campo obligatorio'),
    code: yup.string().min(1, 'Demasiado corto').max(50, 'Demasiado largo').required('Campo obligatorio'),
    ipAddress: yup.string().min(1, 'Demasiado corto').max(50, 'Demasiado largo').nullable(),
    users: yup.array().of(yup.string()).nullable(),
    departments: yup.array().of(yup.string()).nullable(),
    parentCommercialNetworkId: yup.string().nullable(),
    email: yup.string().email('Email inválido').nullable().notRequired(),
    telephone: yup.string().nullable().notRequired(),
    corporations: yup.array().of(yup.string()).nullable(),
    commissionRules: yup.array().of(
        yup.object({
            productTypeId: yup.string().required('Campo obligatorio'),
            saleType: yup.string().oneOf(['recruitment', 'renovation']).required('Campo obligatorio'),
            triggerType: yup.string().oneOf(['contract_creation', 'invoice_collection']).required('Campo obligatorio'),
            commissionPercentage: yup.number().min(0, 'No puede ser negativo').required('Campo obligatorio'),
        })
    ).nullable(),
});

const NetworkForm: React.FC<CreateFormProps> = ({ id, setNodeId, isOpen, setIsOpen, refetch }) => {

    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { getNetworksList } = useNetworks({ active: true });
    const { getProductTypesList } = useProductTypes({ active: true });
    const { getUsersList } = useUsers({ active: true });
    const { getDepartmentsList } = useDepartments({ active: true });
    const { getUnassignedDepartmentsList } = useUnassignedDepartments({ active: true });
    const { getOrganisingCorporationsList } = useOrganisingCorporations({ active: true });
    const service = new NetworkService();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const emptyCommissionRule = {
        productTypeId: '',
        saleType: 'recruitment',
        triggerType: 'contract_creation',
        commissionPercentage: 0,
    };

    const [data] = useFetch(useCallback(async () => {
        if (!id) return null;
        const response = await service.getNetworkById(id as string);
        return response.getResponseData() as Network;
    }, [id]));

    const mode = data ? 'Editar' : 'Crear';

    const submit = async (values: any) => {
        setIsLoading(true);

        if (mode === 'Crear') {
            try {
                const response = await service.createNetwork(values);
                const responseData = response.getResponseData();

                if (responseData.success) {
                    toast.success("Red comercial creada correctamente");
                    refetch();
                    setIsOpen(false);
                } else {
                    responseData.data.errors.forEach((error: any) => {
                        toast.error(error.message);
                    });
                }
            } catch (error: any) {
                toast.error(error.message);
            } finally {
                setIsLoading(false);
            }
        } else {
            try {
                const response = await service.editNetwork(values);
                const responseData = response.getResponseData();

                if (responseData.success) {
                    toast.success("Red comercial editada correctamente");
                    refetch();
                    setIsOpen(false);
                } else {
                    responseData.data.errors.forEach((error: any) => {
                        toast.error(error.message);
                    });
                }
            } catch (error: any) {
                toast.error(error.message);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleDelete = async (id: string) => {
        const response = await (await service.deleteNetwork(id)).getResponseData();
        if (response.success) {
            toast.success('Red comercial eliminada correctamente');
            setIsOpen(false);
            setNodeId(undefined);
            refetch();
        } else {
            handleErrors(response);
        }
    };

    const formik = useFormik({
        initialValues: {
            commercialNetworkId: id ?? '',
            name: data?.name ?? '',
            code: data?.code ?? '',
            ipAddress: data?.ipAddress ?? '',
            users: data?.user?.map((u: any) => u.id) ?? [],
            departments: data?.department?.map((d: any) => d.id) ?? [],
            parentCommercialNetworkId: data?.parentCommercialNetwork?.id ?? null,
            email: data?.email ?? '',
            telephone: data?.telephone ?? '',
            corporations: data?.corporations?.map((c: any) => c.id) ?? [],
            commissionRules: data?.commissionRules?.map((rule: any) => ({
                productTypeId: rule.productTypeId ?? '',
                saleType: rule.saleType ?? 'recruitment',
                triggerType: rule.triggerType ?? 'contract_creation',
                commissionPercentage: Number(rule.commissionPercentage ?? 0),
            })) ?? [],
        },
        validationSchema: schema,
        validateOnBlur: false,
        //validateOnChange: false,
        onSubmit: values => { submit(values); },
    });

    useEffect(() => {
        if (data) {
            formik.setValues({
                commercialNetworkId: data.id,
                name: data.name,
                code: data.code,
                ipAddress: data.ipAddress,
                users: data.user?.map((u: any) => u.id),
                departments: data.departments?.map((d: any) => d.department?.id),
                parentCommercialNetworkId: data.parentCommercialNetwork?.id ?? null,
                email: data.email,
                telephone: data.telephone,
                corporations: data.corporations?.map((c: any) => c.id),
                commissionRules: data.commissionRules?.map((rule: any) => ({
                    productTypeId: rule.productTypeId ?? '',
                    saleType: rule.saleType ?? 'recruitment',
                    triggerType: rule.triggerType ?? 'contract_creation',
                    commissionPercentage: Number(rule.commissionPercentage ?? 0),
                })) ?? [],
            });
        }
    }, [data]);

    useEffect(() => {
        if (id === undefined) {
            formik.resetForm();
        }
    }, [id]);

    if (isOpen && id && !data) return <Loader />;

    const saleTypeOptions = [
        { value: 'recruitment', label: 'Captación' },
        { value: 'renovation', label: 'Renovación' },
    ];

    const triggerTypeOptions = [
        { value: 'contract_creation', label: 'Alta de contrato' },
        { value: 'invoice_collection', label: 'Cobro de factura' },
    ];

    const getContent = () => {
        return (
            <div className="@container">
                <form onSubmit={formik.handleSubmit}>
                    <div className="mb-10 grid gap-3 @2xl:gap-9 @3xl:gap-11 font-medium">
                        {id && isOpen && userCan('delete_commercial_network', 'commercial_network') && (
                            <div className="flex justify-between gap-10 pt-2">

                                <div>
                                    <div className="text-dark-500">Información General</div>
                                    <div className="text-sm text-gray-500">Datos de la red comercial</div>
                                </div>

                                <ButtonPopover
                                    title='Eliminar red comercial'
                                    description='Esto eliminará todo lo que dependa de esta red comercial'
                                    onClick={() => handleDelete(id)}
                                    variant='text'
                                    mobileView
                                    btnClassName=" px-4 py-2 gap-2"
                                />
                            </div>
                        )}

                        <RequiredInput
                            id='name'
                            label="Nombre"
                            formik={formik}
                        />

                        <div className="space-y-2">
                            <CustomSelect
                                isMulti
                                isSearchable
                                id={'users'}
                                label="Usuarios"
                                value={formik.values.users ? getUsersList().filter((u: any) => formik.values.users.includes(u.value)) : []}
                                options={getUsersList()}
                                onChange={(e: any) => {
                                    const selectedIds = e ? e.map((option: any) => option.value) : [];
                                    formik.setFieldValue('users', selectedIds);
                                }}
                                isDisabled={!userCan('get_users_for_select', 'user')}
                            />
                            {!userCan('get_users_for_select', 'user') && (
                                <div className="flex items-center gap-2 text-red-500 text-sm">
                                    <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                    <span>No tienes permisos para ver los usuarios</span>
                                </div>
                            )}
                        </div>

                        <CustomSelect
                            isMulti
                            isSearchable
                            id={'departments'}
                            label="Departamentos"
                            value={formik.values.departments ? getDepartmentsList().filter((d: any) => formik.values.departments.includes(d.value)) : []}
                            options={getUnassignedDepartmentsList()}
                            onChange={(e: any) => {
                                const selectedIds = e ? e.map((option: any) => option.value) : [];
                                formik.setFieldValue('departments', selectedIds);
                            }}
                        />

                        <CustomSelect
                            isSearchable
                            isClearable
                            id={'parentCommercialNetworkId'}
                            label="Red Comercial Padre"
                            value={{ value: formik.values.parentCommercialNetworkId, label: getNetworksList()?.find((g: any) => g.value === formik.values.parentCommercialNetworkId)?.label }}
                            options={getNetworksList().filter((n: any) => n.value !== id)}
                            onChange={(e: any) => { formik.setFieldValue('parentCommercialNetworkId', e?.value); }}
                        />

                        <CustomSelect
                            isMulti
                            isSearchable
                            id={'corporations'}
                            label="Empresas organizadoras"
                            value={formik.values.corporations ? getOrganisingCorporationsList().filter((c: any) => formik.values.corporations.includes(c.value)) : []}
                            options={getOrganisingCorporationsList()}
                            onChange={(e: any) => {
                                const selectedIds = e ? e.map((option: any) => option.value) : [];
                                formik.setFieldValue('corporations', selectedIds);
                            }}
                        />

                        <RequiredInput
                            id='code'
                            label="Código"
                            formik={formik}
                        />

                        <RequiredInput
                            id='ipAddress'
                            label="Dirección IP"
                            required={false}
                            formik={formik}
                        />

                        <RequiredInput
                            id='email'
                            type={"email"}
                            label="Correo Electrónico"
                            required={false}
                            formik={formik}
                        />

                        <PhoneInput
                            id='telephone'
                            label="Teléfono"
                            onChange={e => { formik.setFieldValue('telephone', e) }}
                            value={formik.values.telephone}
                            error={formik.errors.telephone as string}
                            required={false}
                        />

                        <FormGroup
                            title="Reglas de comisión de red"
                            description="Define comisiones por tipo de producto, tipo de venta y momento de disparo."
                            className="pt-2"
                            titleCols="@md:col-span-3"
                            childCols="@md:col-span-9 md:grid-cols-1"
                        >
                            <div className="space-y-3">
                                {formik.values.commissionRules?.length > 0 && formik.values.commissionRules.map((rule: any, index: number) => (
                                    <div key={`commission-rule-${index}`} className="grid grid-cols-1 md:grid-cols-12 gap-3 border border-gray-200 rounded-md p-3">
                                        <div className="md:col-span-4">
                                            <CustomSelect
                                                id={`commissionRules[${index}].productTypeId`}
                                                label="Tipo de producto"
                                                isSearchable
                                                options={getProductTypesList()}
                                                value={getProductTypesList().find((p: any) => p.value === rule.productTypeId) ?? null}
                                                onChange={(option: any) => {
                                                    formik.setFieldValue(`commissionRules[${index}].productTypeId`, option?.value ?? '');
                                                }}
                                            />
                                        </div>

                                        <div className="md:col-span-2">
                                            <CustomSelect
                                                id={`commissionRules[${index}].saleType`}
                                                label="Tipo venta"
                                                isSearchable
                                                options={saleTypeOptions}
                                                value={saleTypeOptions.find((option) => option.value === rule.saleType) ?? null}
                                                onChange={(option: any) => {
                                                    formik.setFieldValue(`commissionRules[${index}].saleType`, option?.value ?? 'recruitment');
                                                }}
                                            />
                                        </div>

                                        <div className="md:col-span-3">
                                            <CustomSelect
                                                id={`commissionRules[${index}].triggerType`}
                                                label="Disparador"
                                                isSearchable
                                                options={triggerTypeOptions}
                                                value={triggerTypeOptions.find((option) => option.value === rule.triggerType) ?? null}
                                                onChange={(option: any) => {
                                                    formik.setFieldValue(`commissionRules[${index}].triggerType`, option?.value ?? 'contract_creation');
                                                }}
                                            />
                                        </div>

                                        <div className="md:col-span-2">
                                            <Input
                                                type="number"
                                                label={<span>% Comisión <span className="text-red-500">*</span></span>}
                                                min={0}
                                                step={0.01}
                                                value={rule.commissionPercentage}
                                                onChange={(event) => {
                                                    const parsed = Number(event.target.value);
                                                    formik.setFieldValue(`commissionRules[${index}].commissionPercentage`, Number.isNaN(parsed) ? 0 : parsed);
                                                }}
                                                inputClassName="text-sm"
                                            />
                                        </div>

                                        <div className="md:col-span-1 flex md:justify-end md:items-end">
                                            <Button
                                                type="button"
                                                color="danger"
                                                size="sm"
                                                onClick={() => {
                                                    const nextRules = formik.values.commissionRules.filter((_: any, idx: number) => idx !== index);
                                                    formik.setFieldValue('commissionRules', nextRules);
                                                }}
                                            >
                                                <FaRegTrashAlt />
                                            </Button>
                                        </div>
                                    </div>
                                ))}

                                <div className="flex justify-end">
                                    <Button
                                        type="button"
                                        color="secondary"
                                        size="sm"
                                        onClick={() => {
                                            formik.setFieldValue('commissionRules', [
                                                ...(formik.values.commissionRules ?? []),
                                                { ...emptyCommissionRule },
                                            ]);
                                        }}
                                    >
                                        <MdAdd className="h-5 w-5 me-1" />
                                        Añadir regla
                                    </Button>
                                </div>
                            </div>
                        </FormGroup>
                    </div>
                </form>
            </div>
        )
    };

    return (
        <>
            {isOpen && (
                <>
                    <Page container="fluid">
                        {(mode === "Editar" && data) && getContent()}
                        {mode === "Crear" && getContent()}
                        <FormFooter
                            customClassName={"border-0 py-1"}
                            customBg={"bg-transparent"}
                            submitBtnText={mode + ' Red Comercial'}
                            handleCancelBtn={() => { setIsOpen(false); setNodeId(undefined); formik.resetForm(); }}
                            handleSubmitBtn={formik.submitForm} isLoading={isLoading}
                        />
                    </Page>
                </>
            )}
        </>
    )
};

export default NetworkForm;
